/*
 * AAIKO collection behaviours. Three custom elements, one file, guarded so the
 * three sections can each request it without redefining anything.
 *
 *   aaiko-collection-toolbar  filter trigger + grid density toggle
 *   aaiko-collection-grid     load-more / infinite scroll via section rendering
 *   aaiko-collection-seo      read more on the collapsed SEO copy
 *
 * Density is a presentation choice, so it is kept in localStorage per
 * breakpoint bucket and re-applied on the next collection page.
 */
(function () {
  'use strict';

  var STORE_KEY = 'aaiko:density';
  var TRAIL_KEY = 'aaiko:plp';
  var TRAIL_TTL = 30 * 60 * 1000;

  /*
   * Where the page actually scrolls. Horizon moves the scroll container by
   * breakpoint: below 990px html/body scroll, at 990 and up base.css hands
   * scrolling to .page-wrapper. Read it from the DOM rather than repeating
   * the breakpoint, so this keeps working if Horizon moves it again.
   */
  function scroller() {
    var wrap = document.querySelector('.page-wrapper');
    if (wrap && wrap.scrollHeight > wrap.clientHeight + 1) return wrap;
    return null;
  }

  function scrollOffset() {
    var host = scroller();
    if (host) return host.scrollTop;
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  /*
   * Instant, never smooth. Horizon sets `scroll-behavior: smooth` on
   * .page-wrapper and on html, so a plain `scrollTop = y` ANIMATES: coming
   * back from a product would scroll the shopper from the top of the
   * collection down to where they were, over a second and a half. Restoring
   * a position is not a scroll gesture, so it must not be animated.
   */
  function scrollToOffset(y) {
    var host = scroller();
    var target = host || window;
    try {
      target.scrollTo({ top: y, behavior: 'instant' });
      return;
    } catch (e) {
      /* engine without CSSOM scrollTo options */
    }
    var el = host || document.documentElement;
    var previous = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    if (host) host.scrollTop = y;
    else window.scrollTo(0, y);
    el.style.scrollBehavior = previous;
  }

  /* Filters and sort live in the query string, so the path plus the query IS
     the identity of a result set: a different filter set simply will not
     match, and gets no restore rather than a wrong one. */
  function trailKey() {
    return window.location.pathname + window.location.search;
  }

  function readDensity() {
    try {
      return localStorage.getItem(STORE_KEY) || 'tight';
    } catch (e) {
      return 'tight';
    }
  }

  function writeDensity(value) {
    try {
      localStorage.setItem(STORE_KEY, value);
    } catch (e) {
      /* private mode: keep the session-only value */
    }
  }

  function grids() {
    return document.querySelectorAll('aaiko-collection-grid');
  }

  function applyDensity(value) {
    grids().forEach(function (grid) {
      grid.setAttribute('data-density', value);
    });
    document.querySelectorAll('[data-aaiko-density]').forEach(function (button) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-aaiko-density') === value));
    });
  }

  if (!customElements.get('aaiko-collection-toolbar')) {
    customElements.define(
      'aaiko-collection-toolbar',
      class AaikoCollectionToolbar extends HTMLElement {
        connectedCallback() {
          this.filterButton = this.querySelector('[data-aaiko-filter-toggle]');
          if (this.filterButton) {
            this.filterButton.addEventListener('click', this.onFilterClick.bind(this));
            document.addEventListener('aaiko:filters:state', this.onFilterState.bind(this));
          }
          this.querySelectorAll('[data-aaiko-density]').forEach(
            function (button) {
              button.addEventListener('click', function () {
                var value = button.getAttribute('data-aaiko-density');
                writeDensity(value);
                applyDensity(value);
              });
            }.bind(this)
          );
          applyDensity(readDensity());
        }

        onFilterClick() {
          var open = this.filterButton.getAttribute('aria-expanded') !== 'true';
          this.filterButton.setAttribute('aria-expanded', String(open));
          document.dispatchEvent(
            new CustomEvent('aaiko:filters:toggle', { detail: { open: open }, bubbles: true })
          );
        }

        /* The filters section may close itself (escape, backdrop, apply). It
           reports back with aaiko:filters:state so the trigger stays truthful. */
        onFilterState(event) {
          if (!event.detail) return;
          this.filterButton.setAttribute('aria-expanded', String(!!event.detail.open));
        }
      }
    );
  }

  if (!customElements.get('aaiko-collection-grid')) {
    customElements.define(
      'aaiko-collection-grid',
      class AaikoCollectionGrid extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('.aaiko-grid__list');
          this.pages = 0;
          this.setAttribute('data-density', readDensity());
          this.bindMore();

          this.remember = this.remember.bind(this);
          /* pagehide fires on the bfcache path too, where beforeunload does
             not, and is the only one Safari on iOS honours reliably. */
          window.addEventListener('pagehide', this.remember);
          document.addEventListener('visibilitychange', this.remember);

          this.restore();
        }

        disconnectedCallback() {
          window.removeEventListener('pagehide', this.remember);
          document.removeEventListener('visibilitychange', this.remember);
        }

        /*
         * Leaving the grid records how far the shopper got: which result set,
         * how many extra pages were pulled in, and the scroll offset. Coming
         * back to the SAME result set replays those pages and lands on the
         * same offset, which is what "terugnavigatie op de oude scrollpositie"
         * asks for. Anything else - a different collection, different filters,
         * a stale trail - is simply not restored.
         */
        remember(event) {
          /* visibilitychange fires in both directions; only the hide edge is
             a departure worth recording. */
          if (event && event.type === 'visibilitychange' && document.visibilityState === 'visible') return;
          try {
            sessionStorage.setItem(
              TRAIL_KEY,
              JSON.stringify({ url: trailKey(), pages: this.pages || 0, scroll: scrollOffset(), at: Date.now() })
            );
          } catch (e) {
            /* private mode, quota: the grid still works, it just does not
               remember. */
          }
        }

        restore() {
          var raw = null;
          try {
            raw = sessionStorage.getItem(TRAIL_KEY);
            /* Read once. A trail that is replayed twice would double-load on
               a reload of the same URL. */
            sessionStorage.removeItem(TRAIL_KEY);
          } catch (e) {
            return;
          }
          if (!raw) return;

          var state;
          try {
            state = JSON.parse(raw);
          } catch (e) {
            return;
          }
          if (!state || state.url !== trailKey()) return;
          if (!state.at || Date.now() - state.at > TRAIL_TTL) return;
          if (!state.pages && !state.scroll) return;

          var self = this;
          /* The observer would race the replay and append pages of its own. */
          if (this.observer) this.observer.disconnect();
          this.setAttribute('data-restoring', '');
          /* The browser restores the offset it recorded for this history
             entry, on a page that was one screen long at the time. Ours is
             the correct one, so take the wheel for the length of the replay
             and hand it back after. */
          var restoration = null;
          try {
            if ('scrollRestoration' in history) {
              restoration = history.scrollRestoration;
              history.scrollRestoration = 'manual';
            }
          } catch (e) {
            restoration = null;
          }

          var step = function (left) {
            if (left <= 0 || !self.link) return Promise.resolve();
            return self.load().then(function () {
              return step(left - 1);
            });
          };

          step(Number(state.pages) || 0)
            .catch(function () {
              /* Fewer pages than before - the collection changed under us.
                 Land on what we did manage to load rather than nothing. */
            })
            .then(function () {
              self.removeAttribute('data-restoring');
              if (self.observer && self.more && self.more.isConnected) self.observer.observe(self.more);
              /* Two frames: the appended cells need a layout pass before the
                 target offset exists. Card media carries an aspect-ratio, so
                 nothing reflows once the images arrive. */
              /*
               * `history.scrollRestoration` governs the DOCUMENT scroll only.
               * Horizon scrolls inside .page-wrapper, and Chrome restores a
               * scrollable ELEMENT's offset on its own schedule, after layout
               * and after our first frames - so a single assignment gets
               * silently overwritten with the offset the page had when it was
               * one screen long. Hold the offset for a short window instead,
               * and give way the moment the shopper touches the page.
               */
              var target = Number(state.scroll) || 0;
              var frames = 0;
              var release = function () {
                try {
                  if (restoration) history.scrollRestoration = restoration;
                } catch (e) {
                  /* nothing to hand back */
                }
                ['wheel', 'touchstart', 'keydown'].forEach(function (type) {
                  window.removeEventListener(type, release, true);
                });
                frames = Infinity;
              };
              ['wheel', 'touchstart', 'keydown'].forEach(function (type) {
                window.addEventListener(type, release, true);
              });

              var hold = function () {
                if (frames === Infinity) return;
                /* Only correct actual drift, so a page nobody is fighting
                   over is left completely alone. */
                if (Math.abs(scrollOffset() - target) > 2) scrollToOffset(target);
                frames += 1;
                if (frames < 150) requestAnimationFrame(hold);
                else release();
              };
              requestAnimationFrame(hold);
            });
        }

        bindMore() {
          this.more = this.querySelector('[data-aaiko-more]');
          this.link = this.querySelector('[data-aaiko-loadmore]');
          if (!this.link) return;

          this.link.addEventListener('click', this.onClick.bind(this));

          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              function (entries) {
                if (entries.some((entry) => entry.isIntersecting)) this.load();
              }.bind(this),
              { rootMargin: '600px 0px' }
            );
            this.observer.observe(this.more);
          }
        }

        onClick(event) {
          event.preventDefault();
          this.load();
        }

        load() {
          if (this.loading || !this.link) return Promise.resolve();
          this.loading = true;
          this.more.setAttribute('data-loading', '');
          var label = this.more.querySelector('.aaiko-grid__morelabel');
          if (label) label.textContent = label.getAttribute('data-loading-label') || label.textContent;

          var url = new URL(this.link.href, window.location.origin);
          url.searchParams.set('section_id', this.getAttribute('data-section-id'));

          return fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            .then(function (response) {
              if (!response.ok) throw new Error('load more failed: ' + response.status);
              return response.text();
            })
            .then(
              function (html) {
                var doc = new DOMParser().parseFromString(html, 'text/html');
                /* Product cells AND the repeat cells that travelled with them,
                   in document order: a repeat is rendered next to the product
                   it repeats, so appending the two together keeps the reading
                   order the server produced. */
                var cells = doc.querySelectorAll(
                  '.aaiko-grid__cell--product, .aaiko-grid__cell--repeat'
                );
                var last = this.list.querySelectorAll('.aaiko-grid__cell--product').length;
                cells.forEach(
                  function (cell) {
                    /* Every cell carries the slot it wants WITHIN its own page.
                       Re-basing is one addition, so the script needs to know
                       nothing about gaps, tiles or page sizes.
                       order keeps promo and note tiles in their drawn slots:
                       product pos*10, repeat pos*10 + slot, tiles pos*10 + 5. */
                    var pos = Number(cell.getAttribute('data-plp-pos')) || 0;
                    if (pos) {
                      var slot = Number(cell.getAttribute('data-plp-slot')) || 0;
                      cell.style.order = String((last + pos) * 10 + slot);
                    }
                    this.list.appendChild(document.importNode(cell, true));
                  }.bind(this)
                );

                this.pages = (this.pages || 0) + 1;

                var nextLink = doc.querySelector('[data-aaiko-loadmore]');
                if (nextLink) {
                  this.link.href = nextLink.getAttribute('href');
                  this.resetMore();
                } else {
                  if (this.observer) this.observer.disconnect();
                  this.more.remove();
                }
              }.bind(this)
            )
            .catch(
              function () {
                this.resetMore();
                /* leave the link in place so the user can retry or navigate */
              }.bind(this)
            );
        }

        /* Leaving the loading state has to put the idle label back, otherwise the
           row keeps reading "Loading..." after a successful append or a retry. */
        resetMore() {
          this.loading = false;
          if (!this.more) return;
          this.more.removeAttribute('data-loading');
          var label = this.more.querySelector('.aaiko-grid__morelabel');
          if (label) label.textContent = label.getAttribute('data-idle-label') || label.textContent;
        }
      }
    );
  }

  if (!customElements.get('aaiko-collection-seo')) {
    customElements.define(
      'aaiko-collection-seo',
      class AaikoCollectionSeo extends HTMLElement {
        connectedCallback() {
          var toggle = this.querySelector('[data-aaiko-seo-toggle]');
          if (!toggle) return;
          toggle.addEventListener(
            'click',
            function () {
              var open = !this.hasAttribute('data-open');
              this.toggleAttribute('data-open', open);
              toggle.setAttribute('aria-expanded', String(open));
              var label = toggle.querySelector('.aaiko-seo__togglelabel');
              if (label) {
                label.textContent = open
                  ? toggle.getAttribute('data-label-less')
                  : toggle.getAttribute('data-label-more');
              }
            }.bind(this)
          );
        }
      }
    );
  }
})();
